import { useState, type ReactNode } from 'react'
import { Link } from 'react-router'
import { useTranslation } from 'react-i18next'
import { type ColumnDef } from '@tanstack/react-table'
import { ArrowRight, TriangleAlert } from 'lucide-react'
import { Button } from '@/components/ui/button'
import { CardContent, CardIcon } from '@/components/ui/card'
import { DataTable } from '@/components/ui/data-table'
import { ListCard } from '@/components/ListCard'
import { ListCardHeader } from '@/components/ListCardHeader'
import { ListTabs } from '@/components/ListTabs'

export interface AttentionTab<Row> {
  key: string
  label: string
  /** One line under the card title saying what the tab lists. */
  hint: ReactNode
  /** What an empty tab says — phrased as good news. */
  empty: string
  rows: Row[]
  loading: boolean
  columns: ColumnDef<Row, unknown>[]
}

interface AttentionCardProps<Row> {
  tabs: AttentionTab<Row>[]
  onRowClick: (row: Row) => void
  /** Remembers column widths dragged by hand, per tab, under this key. */
  storageKey: string
  /** Where "View all" goes. Hidden when left out. */
  viewAllHref?: string
}

/**
 * "Needs attention" card for a module's home page: a tab per kind of problem,
 * each with its count, and a short list sorted most urgent first. What counts
 * as a problem, and how it is shown, is the caller's.
 */
export function AttentionCard<Row>({
  tabs,
  onRowClick,
  storageKey,
  viewAllHref,
}: AttentionCardProps<Row>) {
  const { t } = useTranslation()
  const [active, setActive] = useState(tabs[0].key)
  const tab = tabs.find((item) => item.key === active) ?? tabs[0]

  return (
    <ListCard tone="attention">
      <ListCardHeader
        title={
          <>
            <CardIcon icon={TriangleAlert} />
            {t('attention.title')}
          </>
        }
        subtitle={tab.hint}
        actions={
          viewAllHref && (
            <Button asChild variant="ghost" size="sm">
              <Link to={viewAllHref}>
                {t('dashboard.viewAll')} <ArrowRight className="size-4" />
              </Link>
            </Button>
          )
        }
      />
      {/* On a row of their own under the title, so they stay put while the
          hint above changes length from tab to tab. */}
      <CardContent className="overflow-x-auto">
        <ListTabs
          tabs={tabs.map((item) => ({
            key: item.key,
            label: item.label,
            count: item.loading ? undefined : item.rows.length,
          }))}
          value={tab.key}
          onValueChange={setActive}
        />
      </CardContent>
      {/* Names wrap rather than push the table wider than a half-width card. */}
      <CardContent className="p-0 [&_td:first-child]:break-words [&_td:first-child]:whitespace-normal">
        <DataTable
          // A fresh table per tab, so a sort picked on one does not carry over
          // to columns another tab does not have.
          key={tab.key}
          columns={tab.columns}
          sizingKey={`attention.${storageKey}.${tab.key}`}
          data={tab.rows}
          loading={tab.loading}
          inset
          emptyMessage={tab.empty}
          onRowClick={onRowClick}
          maxHeight={420}
        />
      </CardContent>
    </ListCard>
  )
}
